import React, { useMemo, useState, useEffect } from 'react'
import moment from 'moment'
import API from '../../../services/API'

import AddIcon from '@mui/icons-material/Add'
import CoronavirusIcon from '@mui/icons-material/Coronavirus'
import BuildCircleIcon from '@mui/icons-material/BuildCircle'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HourglassTopIcon from '@mui/icons-material/HourglassTop'
import SearchIcon from '@mui/icons-material/Search'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

// MUI Imports
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import { useNavigate } from 'react-router-dom'
import {
  Avatar,
  Chip,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import KanbanStatus from '../../../components/Kanban/MixedViews'
import { getStatusColor } from '../../../components/Kanban/KanbanTab'
import {
  DEFECT_CLOSED_STATUSES,
  DEFECT_STATUSES,
  isDefectClosed,
} from './statuses'

const getAssigneeName = (assignedTo) =>
  [assignedTo?.firstName, assignedTo?.lastName].filter(Boolean).join(' ')

const getDefectType = (defect) =>
  defect.vehicle
    ? 'Vehicle'
    : defect.medicalDevice
      ? 'Medical Device'
      : defect.premises
        ? 'Premises'
        : defect.asset
          ? 'Equipment'
          : 'Other'

const StatCard = ({ icon, label, value, color, active, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 2,
      border: '1px solid',
      borderColor: active ? `${color}.main` : 'divider',
      bgcolor: active ? 'anMuted.main' : 'background.paper',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'border-color 150ms ease, background-color 150ms ease',
      '&:hover': {
        borderColor: `${color}.main`,
        bgcolor: 'anMuted.main',
      },
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: `${color}.main`,
          color: `${color}.contrastText`,
        }}
      >
        {icon}
      </Avatar>
      <Box minWidth={0}>
        <Typography variant="h5" lineHeight={1.05}>
          {value}
        </Typography>
        <Typography variant="body2" noWrap>
          {label}
        </Typography>
      </Box>
    </Stack>
  </Paper>
)

const Defects = () => {
  const [loading, setLoading] = useState(true)
  const [defects, setDefects] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [firstLoadDone, setFirstLoadDone] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const navigate = useNavigate()

  const loadDefects = () => {
    setLoading(true)
    API.get(
      `/defects?includeCompleted=${selectedStatuses.includes('Completed')}`,
    )
      .then((res) => {
        if (res.status === 200) {
          setDefects(
            res.data
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .reduce((accumulator, defect) => {
                accumulator.push({
                  id: defect._id,
                  defectNumber: defect?.defectNumber || null,
                  date: defect.createdAt,
                  status: defect.status,
                  type: getDefectType(defect),
                  summary:
                    defect.summary?.trim() ||
                    defect.description?.trim()?.substring(0, 80) ||
                    'No Description!',
                  assignedTo: defect.assignedTo,
                  assignedToName: getAssigneeName(defect.assignedTo),
                  isIPC: defect.isIPC,
                  vehicle: defect.vehicle,
                  asset: defect.asset,
                  vdi: defect.vdi,
                })
                return accumulator
              }, []),
          )
          if (!firstLoadDone) {
            if (res.data.some((r) => r.status === 'New')) {
              setSelectedStatuses((prev) => [...prev, 'New'])
            }
            if (res.data.some((r) => r.status === 'In Progress')) {
              setSelectedStatuses((prev) => [...prev, 'In Progress'])
            }
            if (res.data.some((r) => r.status === 'Waiting')) {
              setSelectedStatuses((prev) => [...prev, 'Waiting'])
            }
            setFirstLoadDone(true)
          }
        }
      })
      .catch((e) => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadDefects()
  }, [selectedStatuses])

  const metrics = useMemo(
    () => ({
      total: defects.length,
      open: defects.filter((defect) => !isDefectClosed(defect.status)).length,
      new: defects.filter((defect) => defect.status === 'New').length,
      waiting: defects.filter((defect) => defect.status === 'Waiting').length,
      completed: defects.filter((defect) => isDefectClosed(defect.status))
        .length,
      ipc: defects.filter((defect) => defect.isIPC).length,
    }),
    [defects],
  )

  const filteredDefects = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    if (!query) return defects

    return defects.filter((defect) =>
      [
        defect.summary,
        defect.status,
        defect.type,
        defect.assignedToName,
        defect.vehicle?.callSign,
        defect.asset?.assetID ?? defect.asset,
      ]
        .filter(Boolean)
        .some((value) => value.toString().toLowerCase().includes(query)),
    )
  }, [defects, searchTerm])

  const toggleStatus = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((selected) => selected !== status)
        : [...prev, status],
    )
  }

  // The Completed tile covers every closed status (duplicates included).
  const closedSelected = DEFECT_CLOSED_STATUSES.some((status) =>
    selectedStatuses.includes(status),
  )
  const toggleClosed = () =>
    setSelectedStatuses((prev) =>
      closedSelected
        ? prev.filter((status) => !isDefectClosed(status))
        : [...prev, ...DEFECT_CLOSED_STATUSES],
    )

  return (
    <Stack spacing={2.5}>
      {loading && <LinearProgress sx={{ borderRadius: 1 }} />}
      {fetchError && (
        <Alert severity="error">
          Unable to load Defects. Please try again later.
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar sx={{ bgcolor: 'contrast.main', color: 'white' }}>
                <BuildCircleIcon />
              </Avatar>
              <Box>
                <Typography variant="title2">Defects</Typography>
                <Typography variant="body2">
                  Track vehicle and equipment defects through to resolution.
                </Typography>
              </Box>
            </Stack>
          </Box>
          <Button
            onClick={() => navigate('/report-defect')}
            variant="contained"
            color="contrast"
            startIcon={<AddIcon />}
            sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}
          >
            Add Defect
          </Button>
        </Stack>

        <Grid container spacing={1.5} marginTop={2}>
          <Grid item xs={6} md={4} lg={2}>
            <StatCard
              icon={<WarningAmberIcon />}
              label="Open"
              value={metrics.open}
              color="warning"
            />
          </Grid>
          <Grid item xs={6} md={4} lg={2}>
            <StatCard
              icon={<BuildCircleIcon />}
              label="New"
              value={metrics.new}
              color="error"
              active={selectedStatuses.includes('New')}
              onClick={() => toggleStatus('New')}
            />
          </Grid>
          <Grid item xs={6} md={4} lg={2}>
            <StatCard
              icon={<HourglassTopIcon />}
              label="Waiting"
              value={metrics.waiting}
              color="anPurple"
              active={selectedStatuses.includes('Waiting')}
              onClick={() => toggleStatus('Waiting')}
            />
          </Grid>
          <Grid item xs={6} md={4} lg={2}>
            <StatCard
              icon={<CheckCircleIcon />}
              label="Completed"
              value={metrics.completed}
              color="success"
              active={closedSelected}
              onClick={toggleClosed}
            />
          </Grid>
          <Grid item xs={6} md={4} lg={2}>
            <StatCard
              icon={<CoronavirusIcon />}
              label="IPC"
              value={metrics.ipc}
              color="info"
            />
          </Grid>
          <Grid item xs={6} md={4} lg={2}>
            <StatCard
              icon={<BuildCircleIcon />}
              label="All records"
              value={metrics.total}
              color="primary"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', lg: 'center' }}
          justifyContent="space-between"
        >
          <TextField
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search defects, assets, vehicles or owners"
            size="small"
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{ maxWidth: { lg: 460 } }}
            fullWidth
          />
          <Stack direction="row" gap={1} flexWrap="wrap">
            {DEFECT_STATUSES.map((status) => (
              <Chip
                key={status}
                label={status}
                color={
                  selectedStatuses.includes(status)
                    ? getStatusColor(status)
                    : 'default'
                }
                variant={
                  selectedStatuses.includes(status) ? 'filled' : 'outlined'
                }
                onClick={() => toggleStatus(status)}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>

      <KanbanStatus
        selectedStatuses={selectedStatuses}
        setSelectedStatuses={setSelectedStatuses}
        statusOptions={DEFECT_STATUSES}
        loadDefects={loadDefects}
        items={filteredDefects}
        columns={[
          {
            field: 'createdAt',
            renderCell: ({ row }) => moment(row.date).format('DD/MM/YYYY'),
            headerName: 'Date',
            valueGetter: (value, row) => moment(row.date).unix(),
          },
          { field: 'defectNumber', headerName: 'Ref' },
          {
            field: 'summary',
            renderCell: ({ row }) =>
              row.summary
                ? row.summary
                : row.description
                  ? row.description
                  : '',
            minWidth: 200,
            flex: 1,
            headerName: 'Summary',
          },
          {
            field: 'type',
            headerName: 'Type',
            minWidth: 130,
            renderCell: ({ value }) => (
              <Chip label={value} size="small" variant="outlined" />
            ),
          },
          {
            field: 'asset',
            headerName: 'Asset',
            minWidth: 120,
            renderCell: ({ row }) =>
              row.vehicle?.callSign || row.asset?.assetID || row.asset || '-',
          },
          {
            field: 'assignedToName',
            headerName: 'Assigned',
            minWidth: 150,
            renderCell: ({ row }) =>
              row.assignedToName ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar
                    src={row.assignedTo?.photograph}
                    sx={{ width: 26, height: 26, fontSize: '0.75rem' }}
                  >
                    {row.assignedToName.charAt(0)}
                  </Avatar>
                  <Typography variant="body2">{row.assignedToName}</Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Unassigned
                </Typography>
              ),
          },
          {
            field: 'isIPC',
            headerName: 'IPC',
            width: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ value }) =>
              value ? (
                <Chip
                  icon={<CoronavirusIcon />}
                  label="IPC"
                  color="info"
                  size="small"
                />
              ) : (
                '-'
              ),
          },
          {
            field: 'status',
            align: 'center',
            headerAlign: 'center',
            headerName: 'Status',
            renderCell: ({ value }) => (
              <Chip
                color={getStatusColor(value)}
                size="small"
                label={value}
                sx={{ borderRadius: 1, width: '100%' }}
              />
            ),
            minWidth: 140,
          },
        ]}
        onItemClick={({ row }) => {
          navigate('/defects/' + row.id)
        }}
      />
    </Stack>
  )
}

export default Defects
